import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Assessment, Measure, Score, Term } from '@td/shared';
import { get, post, patch, put, del } from '../lib/api';

const KEY = ['assessments'];
const scoresKey = (id: string) => ['assessment-scores', id];

/**
 * The current year's assessments, or a past year's when one is named.
 *
 * The year is part of the query key: a teacher flicking between this year and
 * last should not see one cached list stand in for the other.
 */
export function useAssessments(schoolYear?: string | null) {
  return useQuery({
    queryKey: schoolYear ? [...KEY, schoolYear] : KEY,
    queryFn: () =>
      get<Assessment[]>(
        schoolYear ? `/assessments?schoolYear=${encodeURIComponent(schoolYear)}` : '/assessments',
      ),
  });
}

export interface AssessmentInput {
  name: string;
  subject?: string;
  termly?: boolean;
  measures: Measure[];
}

export function useCreateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssessmentInput) => post<Assessment>('/assessments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<AssessmentInput> & { id: string; archived?: boolean }) =>
      patch<Assessment>(`/assessments/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<{ scoresRemoved: number }>(`/assessments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useScores(assessmentId: string | null) {
  return useQuery({
    queryKey: scoresKey(assessmentId ?? 'none'),
    queryFn: () => get<Score[]>(`/assessments/${assessmentId}/scores`),
    enabled: !!assessmentId,
  });
}

export interface ScoreWrite {
  measureId: string;
  studentId: string;
  term?: Term | null;
  date?: string;
  value?: number | null;
  level?: string | null;
  note?: string;
}

export type SaveScores = ReturnType<typeof useSaveScores>;

export function useSaveScores(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scores: ScoreWrite[]) =>
      put<Score[]>(`/assessments/${assessmentId}/scores`, { scores }),
    // The response is every score for the assessment, so it replaces the cache
    // outright rather than triggering a refetch behind the next keystroke.
    onSuccess: (rows) => qc.setQueryData(scoresKey(assessmentId), rows),
  });
}
